// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { isServerBackedAnalysisId } from '../../../utils/isServerBackedAnalysisId';
import { build280gAnalysisResults } from '../utils/build280gAnalysisResults';
import { build280gRunSnapshotPayload } from '../utils/build280gRunSnapshot';
import { recordAnalysisRun, generateAllAnalysisDocuments } from '../../../services/AnalysisApiClient';

interface CalculateNotice {
  variant: 'success' | 'danger';
  text: string;
}

interface UseCalculateRunOptions {
  formData: Record<string, unknown>;
  currentAnalysisId: string | null;
  currentAnalysis: { lastRunAt?: string | null; workflowLastChangedAt?: string | null } | null;
  setAnalyses: (updater: (prev: any[]) => any[]) => void;
  isCurrentAnalysisLocked: boolean;
}

export function formatLastRunAt(raw: unknown): string | null {
  if (raw == null) return null;
  const d = new Date(String(raw));
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

export function useCalculateRun({
  formData,
  currentAnalysisId,
  currentAnalysis,
  setAnalyses,
  isCurrentAnalysisLocked,
}: UseCalculateRunOptions) {
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculateNotice, setCalculateNotice] = useState<CalculateNotice | null>(null);
  const [lastRunLabel, setLastRunLabel] = useState<string | null>(null);

  const isRunStale = (() => {
    const lr = currentAnalysis?.lastRunAt;
    const wf = currentAnalysis?.workflowLastChangedAt;
    if (!wf) return false;
    if (!lr) return true;
    const tLast = new Date(String(lr)).getTime();
    const tWf = new Date(String(wf)).getTime();
    if (Number.isNaN(tLast) || Number.isNaN(tWf)) return false;
    return tLast < tWf;
  })();

  useEffect(() => {
    const analysisId = String(currentAnalysisId ?? '').trim();
    if (!isServerBackedAnalysisId(analysisId)) {
      setLastRunLabel(null);
      return;
    }
    setLastRunLabel(formatLastRunAt(currentAnalysis?.lastRunAt));
  }, [currentAnalysisId, currentAnalysis?.lastRunAt]);

  const handleCalculate = useCallback(async () => {
    setCalculateNotice({ variant: 'success', text: 'Calculating…' });
    setIsCalculating(true);
    try {
      const analysisId = String(currentAnalysisId ?? '').trim();
      if (isCurrentAnalysisLocked) {
        setCalculateNotice({
          variant: 'danger',
          text: 'This analysis is final and cannot be edited or re-run.',
        });
        return;
      }
      if (!isServerBackedAnalysisId(analysisId)) {
        setCalculateNotice({ variant: 'danger', text: 'Please open a saved analysis first.' });
        return;
      }

      const results = build280gAnalysisResults(formData || {});
      const individualsCount = Array.isArray(results) ? results.length : 0;
      if (individualsCount <= 0) {
        setCalculateNotice({ variant: 'danger', text: 'Add at least one individual before calculating.' });
        return;
      }

      const overCount = results.filter((r) => r?.status === 'over').length;
      const status = overCount > 0 ? 'over_threshold' : 'ok';
      const snapshot = build280gRunSnapshotPayload(formData || {});
      const { runId } = await recordAnalysisRun(analysisId, {
        status,
        individualsCount,
        overCount,
        snapshot,
      });

      const now = new Date();
      const isoUtc = now.toISOString();
      setAnalyses((prev) =>
        prev.map((a) =>
          a.id === analysisId ? { ...a, lastRunAt: isoUtc, lastRunId: runId ?? a.lastRunId ?? null } : a
        )
      );
      setLastRunLabel(formatLastRunAt(isoUtc));

      let docNotice = '';
      if (runId) {
        try {
          const docs = await generateAllAnalysisDocuments(analysisId, runId);
          const readyCount = docs.filter((d) => d.status === 'Ready').length;
          docNotice = readyCount > 0 ? ` Generated ${readyCount} document${readyCount === 1 ? '' : 's'}.` : '';
        } catch (docErr) {
          console.error('Document generation failed', docErr);
          docNotice = ' Documents could not be generated — open the Documents tab to retry.';
        }
      }

      setCalculateNotice({
        variant: 'success',
        text: `Calculation completed and run recorded.${docNotice}`,
      });
    } catch (e) {
      console.error('Failed to record analysis run', e);
      const message = e instanceof Error && e.message.trim() ? e.message.trim() : '';
      setCalculateNotice({
        variant: 'danger',
        text: message || 'Could not calculate or record analysis run. Please review inputs and try again.',
      });
    } finally {
      setIsCalculating(false);
    }
  }, [currentAnalysisId, formData, isCurrentAnalysisLocked, setAnalyses]);

  return {
    isCalculating,
    calculateNotice,
    lastRunLabel,
    isRunStale,
    handleCalculate,
  };
}
